import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  Dropdown as PrimeDropdown,
  type DropdownChangeEvent,
  Dropdown as PrimeDropdownType,
  type DropdownProps as PrimeDropdownProps,
} from 'primereact/dropdown';
import { Tooltip } from '../../Tooltip/Tooltip';
import styles from './Dropdown.module.scss';
import { LoadingSpinner } from '../../LoadingSpinner';
import { Icon } from '../../Icon/Icon';
import { Flags } from '../../Flags/Flags';

/**
 * DropdownState defines the various states of dropdown.
 */
type DropdownState = 'default' | 'open';

/** Properties for Dropdown option */
interface DropdownOption {
  id: string;
  label: string | ReactNode;
  filterText?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const DropdownOptionItem = ({
  option,
  showTooltip,
}: {
  option: DropdownOption;
  showTooltip: boolean;
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (elementRef.current) {
      setIsTruncated(elementRef.current.scrollWidth > elementRef.current.clientWidth);
    }
  }, [option.label]);

  return (
    <div
      ref={elementRef}
      data-target={`dropdown-option-${option.id}`}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {option.label}
      {isTruncated && showTooltip && (
        <Tooltip
          tooltipText={option.filterText}
          tooltipTarget={`dropdown-option-${option.id}`}
          arrowPosition="Right"
          pointer={false}
        />
      )}
    </div>
  );
};

/**
 * Properties for the Dropdown component.
 * @property {DropdownOption[]} options - The list of dropdown options to display.
 * @property {DropdownState} [state] - Optional state of the dropdown (e.g., Default, Open).
 * @property {string} [placeholder] - Optional placeholder text displayed when no option is selected.
 * @property {string} [defaultValue] - Optional default value to pre-select an option.
 * @property {(option: DropdownOption) => void} onSelect - Callback function triggered when an option is selected.
 * @property {boolean} [showFlagDropdown] - Optional flag to show the flag dropdown.
 * @property {boolean} [hasFavorites] - Optional flag for the inclusion of favorites functionality
 * @property {string} [dropdownPanelClass] - Custom unique class for dropdown panel
 * @property {string} [dropdownClass] - Custom unique class for dropdown component
 * @property {string | HTMLElement} [dropdownAppendTo] - Optional target element or selector to which the dropdown panel should be appended. Defaults to the parent element.
 * @property {string} [customFeedbackPlaceholder] - Optional placeholder for a custom option input rendered below the option list.
 * @property {(value: string) => void} [onCustomFeedbackChange] - Callback triggered when the custom option input changes.
 * @property {Partial<PrimeDropdownProps>} [dropdownProps] - Additional props to pass to the underlying PrimeReact Dropdown component.
 */
export interface DropdownProps {
  options: DropdownOption[];
  state?: DropdownState;
  placeholder?: string;
  defaultValue?: string;
  onSelect: (option: DropdownOption) => void;
  showFlagDropdown?: boolean;
  hasFavorites?: boolean;
  dropdownPanelClass?: string;
  dropdownClass?: string;
  dropdownAppendTo?: HTMLElement | 'self' | null;
  customFeedbackPlaceholder?: string;
  onCustomFeedbackChange?: (value: string) => void;
  dropdownProps?: Partial<PrimeDropdownProps>;
}

/**
 * Custom Dropdown component that uses PrimeReact's Dropdown. It supports features like recent
 * selections, favorites, and an optional flag dropdown mode.
 *
 * ## Import path
 * ```tsx
 * import { Dropdown } from '@kpmg-global-audit-emu/react-ui-kit/components/AuditFramework/Dropdown';
 * ```
 * @param {DropdownProps} props - Props for the Dropdown component.
 * @returns {JSX.Element} The rendered Dropdown component.
 */
export const Dropdown = ({
  options,
  state,
  placeholder,
  defaultValue,
  onSelect,
  showFlagDropdown,
  hasFavorites,
  dropdownPanelClass,
  dropdownClass,
  dropdownAppendTo,
  customFeedbackPlaceholder,
  onCustomFeedbackChange,
  dropdownProps,
}: DropdownProps): ReactElement => {
  const [selectedOption, setSelectedOption] = useState<string | null>(defaultValue || null);
  const [customFeedbackValue, setCustomFeedbackValue] = useState('');
  const dropdownRef = useRef<PrimeDropdownType | null>(null);
  const [recentFavorites, setRecentFavorites] = useState<string[]>([]);

  /**
   * Handles the change event when a new option is selected from the dropdown.
   * @param e - The change event containing the selected value.
   */
  const handleChange = (e: DropdownChangeEvent) => {
    const id = e.value as string;

    setSelectedOption(id);
    if (id !== 'custom') {
      setCustomFeedbackValue('');
    }

    const selected = processedOptions.find((opt) => opt.id === id);
    if (selected) {
      onSelect(selected);
    }
  };

  /**
   * Handles the toggle event for the favorite icon
   * @param id - The ID of the option being toggled as favorite
   */
  const handleFavoriteToggle = (id: string) => {
    setRecentFavorites((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id); // remove (unfavorite)
      }
      return [id, ...prev]; // add (favorite)
    });
  };

  // Template for rendering each dropdown option, including tooltip for truncated text
  const itemTemplate = (option: DropdownOption) => {
    if (!option) {
      return <LoadingSpinner animationDuration={2000} size="Medium" type="Spinner" />;
    }
    const isSelected = option.id === selectedOption;
    const showCheck = !hasFavorites && isSelected && !showFlagDropdown;

    return (
      <>
        <DropdownOptionItem option={option} showTooltip={!showFlagDropdown} />

        {/* custom checkmark only when hasFavorites is false */}
        {showCheck && <div className={styles.checkIcon}></div>}
      </>
    );
  };

  // Template for rendering the selected value in the dropdown input field
  const valueTemplate = (option: DropdownOption) => {
    if (!option) {
      if (selectedOption === 'custom' && customFeedbackValue.trim()) {
        return <span>{customFeedbackValue.trim()}</span>;
      }
      if (showFlagDropdown) {
        return <Flags selectedCountryName="United States (US)" shape="Circle" size="X-Small" />;
      }
      return <span>{placeholder}</span>;
    }

    if (showFlagDropdown && isValidElement(option.label)) {
      const props = (option.label as any).props;
      if (props?.prefix) return <div>{props.prefix}</div>;
      else {
        return <span>{option.filterText}</span>;
      }
    }

    return hasFavorites ? (
      <span>{option.filterText}</span>
    ) : (
      <div className={styles.languageDropdown}>
        <Icon name="Browser" shape="Circle" size="Large" type="Base Icon" variant="Outline" />
        <div>
          {!hasFavorites && <span>Language: </span>}
          <span>{option.filterText}</span>
        </div>
      </div>
    );
  };

  // Content to be rendered inside the dropdown options, with favorite toggle functionality
  const processedOptions = useMemo(() => {
    return options.map((option) => {
      if (!isValidElement(option.label)) return option;

      const element = option.label as ReactElement<any>;
      const props = element.props;

      const updatedItems = props.items?.map((item: any) => ({
        ...item,
        data: {
          ...item.data,
          action: {
            ...item.data.action,
            onClick: (e: any) => {
              if (item.data.action.icon === 'Heart') {
                e?.stopPropagation();
                handleFavoriteToggle(option.id);
              }
            },
            filled: recentFavorites.includes(option.id),
          },
        },
      }));

      return {
        ...option,
        label: React.cloneElement(element, {
          ...props,
          items: updatedItems,
        }),
      };
    });
  }, [options, recentFavorites]);

  const customFeedbackFooter = customFeedbackPlaceholder ? (
    <div className={styles.customFeedbackFooter} onMouseDown={(event) => event.stopPropagation()}>
      <input
        type="text"
        className={styles.customFeedbackInput}
        value={customFeedbackValue}
        placeholder={customFeedbackPlaceholder}
        aria-label={customFeedbackPlaceholder}
        onChange={(event) => {
          const value = event.target.value;
          setCustomFeedbackValue(value);
          setSelectedOption(value.trim() ? 'custom' : null);
          onCustomFeedbackChange?.(value);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      />
    </div>
  ) : null;

  const panelFooterTemplate = customFeedbackFooter ? (
    <>
      {customFeedbackFooter}
      {dropdownProps?.panelFooterTemplate}
    </>
  ) : dropdownProps?.panelFooterTemplate;

  // Sort options based on recent favorites and selections, ensuring no duplicates
  const sortedOptions = useMemo(() => {
    const favSet = new Set(recentFavorites);

    const favs = recentFavorites
      .map((id) => processedOptions.find((opt) => opt.id === id))
      .filter(Boolean);

    const rest = processedOptions.filter((opt) => !favSet.has(opt.id));

    return [...favs, ...rest];
  }, [processedOptions, recentFavorites]);

  useEffect(() => {
    setSelectedOption(defaultValue || null);

    if (defaultValue) {
      const selected = options.find((opt) => opt.id === defaultValue);
      if (selected) {
        onSelect(selected);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  useEffect(() => {
    if (state === 'open') {
      dropdownRef.current?.show();
    } else {
      dropdownRef.current?.hide();
    }
  }, [state]);

  return (
    <div
      className={`${styles['dropdown-container']} ${state === 'open' ? styles.open : ''}  ${showFlagDropdown ? styles['flag-dropdown'] : ''
        }`}
    >
      <PrimeDropdown
        {...dropdownProps}
        ref={dropdownRef}
        value={selectedOption}
        options={showFlagDropdown || !hasFavorites ? processedOptions : sortedOptions}
        onChange={handleChange}
        placeholder={placeholder}
        optionLabel="label"
        optionValue="id"
        itemTemplate={itemTemplate}
        valueTemplate={valueTemplate}
        panelFooterTemplate={panelFooterTemplate}
        className={[dropdownClass, dropdownProps?.className].filter(Boolean).join(' ')}
        appendTo={dropdownAppendTo ?? document.body}
        pt={{
          panel: {
            className: `${styles['dropdown-panel']} ${showFlagDropdown ? styles['flag-dropdown-panel'] : ''
              } ${!hasFavorites && !showFlagDropdown ? styles.noFavorites : ''} ${dropdownPanelClass ?? ''}`,
            style: {
              '--p-focus-ring-shadow': 'none',
              '--p-focus-ring-width': '0px',
              '--p-focus-ring-color': 'transparent',
              '--p-select-option-focus-background': 'transparent',
              '--p-select-option-selected-background': 'transparent',
              '--p-select-option-selected-focus-background': 'transparent',
              '--p-select-option-focus-border-color': 'transparent',
              '--p-select-option-selected-focus-border-color': 'transparent',
              '--p-select-option-selected-border-color': 'transparent',
            },
          },
        }}
        {...(options.length > 8
          ? {
            filter: true,
            filterBy: 'filterText',
            filterIcon: (
              <Icon
                name="Search"
                shape="Square"
                size="Medium"
                type="Base Icon"
                variant="Outline"
              />
            ),
          }
          : {})}
      />
    </div>
  );
};
